<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.bulletins', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton element="InertiaLink" :href="meta.links.create" v-if="meta.can.create">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <div>
            <form :action="meta.links.index" method="GET" @submit.prevent="filter" class="p-4" id="bulletin_form">
                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.groups')" for="group_ids" />
                            <SlideOverCheckboxInput v-model="form.group_ids" :options="options.groups">
                                <div class="flex justify-end">
                                    <div class="mt-1">
                                        <SubmitFormButton
                                            form="bulletin_form"
                                            :button-text="$t('messages.filter')"
                                            :processing="form.processing"
                                            is-filter
                                        />
                                    </div>
                                </div>
                            </SlideOverCheckboxInput>
                            <FormError :value="form.errors.group_ids" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                        <div class="flex justify-end space-x-2">
                            <SubmitFormButton
                                :button-text="$t('messages.filter')"
                                :processing="form.processing"
                                is-filter
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <EmptyState picture v-if="bulletins.data.length === 0" />
        <div class="flex flex-col mt-5" v-else>
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.title') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.groups', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(bulletin, index) in bulletins.data"
                                :key="index"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ bulletin.title }}
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-600"
                                >
                                    <div>
                                        {{ bulletin.published_at }}
                                    </div>
                                    <div>
                                        {{ bulletin.unpublished_at }}
                                    </div>
                                </td>
                                <td class="max-w-sm truncate px-6 py-4 text-sm leading-5 font-medium text-gray-600">
                                    {{ bulletin.group_names }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton action="show" :href="bulletin.links.show" />
                                    <InertiaSquareLinkButton
                                        action="edit"
                                        :href="bulletin.links.edit"
                                        v-if="bulletin.can.edit"
                                    />
                                    <InertiaSquareDeleteButton
                                        :href="bulletin.links.delete"
                                        :confirm-text="$t('messages.are_you_sure?')"
                                        v-if="bulletin.can.delete"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="bulletins" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';

export default {
    components: {
        SlideOverCheckboxInput,
        SubmitFormButton,
        FormLabel,
        FormError,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        InertiaSquareDeleteButton,
        AppButton,
        EmptyState,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'bulletins', 'options', 'filters'],
    data() {
        return {
            form: this.$inertia.form({
                group_ids: this.filters.group_ids,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
